import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { useRelay } from "../hub/relay-provider.js";
import { MarkdownContent } from "./ChannelView.js";
export function ThreadPanel({ workspaceId, channelId, thread, rootMessage, authorName, displayName, authorId = null, mentionRegex, onClose, canResolve = false, onThreadUpdated, }) {
    const ctx = useDataContext();
    const relay = useRelay();
    const [resolving, setResolving] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    const stampName = displayName ?? authorName;
    useEffect(() => {
        let cancelled = false;
        setMessages([]);
        setError(null);
        ctx.hub
            .listThreadMessages(workspaceId, channelId, thread.id)
            .then((ms) => {
            if (!cancelled)
                setMessages(ms);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId, channelId, thread.id]);
    // Live replies — same frame family as the parent feed, filtered to this
    // thread. Dedupe by id so the sender's own POST result and its relay
    // echo collapse.
    useEffect(() => {
        return relay.onMessage((msg) => {
            if (msg.type !== "channel_message")
                return;
            if (msg.channel_id !== channelId || msg.thread_id !== thread.id)
                return;
            const id = typeof msg.id === "string" ? msg.id : typeof msg.message_id === "string" ? msg.message_id : null;
            if (!id || typeof msg.content !== "string")
                return;
            const candidate = {
                id,
                channel_id: channelId,
                author_type: msg.author_type ?? "human",
                author_id: typeof msg.author_id === "string" ? msg.author_id : null,
                author_name: typeof msg.author === "string" ? msg.author : typeof msg.author_name === "string" ? msg.author_name : "unknown",
                content: msg.content,
                metadata: null,
                created_at: typeof msg.timestamp === "string" ? msg.timestamp : new Date().toISOString(),
                thread_id: thread.id,
            };
            setMessages((ms) => (ms.some((m) => m.id === candidate.id) ? ms : [...ms, candidate]));
        });
    }, [relay, channelId, thread.id]);
    // Pin to bottom on new replies.
    useEffect(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [messages.length]);
    const send = useCallback(async () => {
        const content = input.trim();
        if (!content || sending)
            return;
        setSending(true);
        setError(null);
        try {
            const sent = await ctx.hub.sendMessage(workspaceId, channelId, {
                content,
                authorName: stampName,
                authorType: "human",
                authorId,
                thread_id: thread.id,
            });
            setMessages((ms) => (ms.some((m) => m.id === sent.id) ? ms : [...ms, sent]));
            setInput("");
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSending(false);
        }
    }, [ctx.hub, workspaceId, channelId, thread.id, input, sending, stampName, authorId]);
    return (_jsxs("div", { style: {
            width: 380,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            borderLeft: "1px solid var(--border)",
            background: "var(--bg-primary)",
            height: "100%",
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderBottom: "1px solid var(--border)",
                    flexShrink: 0,
                }, children: [_jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [_jsx("div", { style: {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "var(--text-primary)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }, children: thread.title }), _jsxs("div", { style: { fontSize: 11, color: "var(--text-muted)" }, children: ["Thread \u00B7 ", messages.length, " ", messages.length === 1 ? "reply" : "replies", thread.resolved_at && (_jsx("span", { style: { color: "var(--success)", marginLeft: 6 }, children: "\u00B7 \u2713 resolved" }))] })] }), canResolve && (_jsx("button", { type: "button", disabled: resolving, onClick: () => {
                            setResolving(true);
                            const call = thread.resolved_at
                                ? ctx.hub.unresolveThread(workspaceId, channelId, thread.id)
                                : ctx.hub.resolveThread(workspaceId, channelId, thread.id);
                            call
                                .then((t) => onThreadUpdated?.(t))
                                .catch((e) => setError(e instanceof Error ? e.message : String(e)))
                                .finally(() => setResolving(false));
                        }, style: {
                            fontSize: 11,
                            padding: "3px 10px",
                            borderRadius: 999,
                            border: "1px solid var(--border)",
                            background: thread.resolved_at ? "transparent" : "var(--bg-tertiary)",
                            color: "var(--text-secondary)",
                            cursor: resolving ? "wait" : "pointer",
                            fontFamily: "inherit",
                            flexShrink: 0,
                        }, children: resolving ? "…" : thread.resolved_at ? "Reopen" : "Resolve" })), _jsx("button", { type: "button", onClick: onClose, "aria-label": "Close thread", style: {
                            background: "none",
                            border: "none",
                            color: "var(--text-muted)",
                            cursor: "pointer",
                            fontSize: 16,
                            padding: 4,
                            lineHeight: 1,
                        }, children: "\u00D7" })] }), _jsxs("div", { ref: scrollRef, style: { flex: 1, overflowY: "auto", padding: "10px 14px" }, children: [rootMessage && (_jsx("div", { style: {
                            paddingBottom: 10,
                            marginBottom: 10,
                            borderBottom: "1px solid var(--border)",
                        }, children: _jsx(ThreadRow, { message: rootMessage, mentionRegex: mentionRegex, ownerName: authorName }) })), messages.map((m) => (_jsx(ThreadRow, { message: m, mentionRegex: mentionRegex, ownerName: authorName }, m.id))), messages.length === 0 && !error && (_jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }, children: "No replies yet." })), error && (_jsx("div", { style: { fontSize: 12, color: "var(--error)", padding: "8px 0" }, children: error }))] }), _jsx("div", { style: { padding: "10px 14px", borderTop: "1px solid var(--border)", flexShrink: 0 }, children: thread.resolved_at ? (_jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", textAlign: "center", padding: "4px 0" }, children: "This thread is resolved and read-only." })) : (_jsx("textarea", { value: input, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            void send();
                        }
                    }, placeholder: `Reply to ${thread.title}…`, rows: 2, style: {
                        width: "100%",
                        resize: "none",
                        padding: "8px 10px",
                        background: "var(--bg-tertiary)",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--text-primary)",
                        fontSize: 13,
                        fontFamily: "inherit",
                        outline: "none",
                        boxSizing: "border-box",
                    } })) })] }));
}
function ThreadRow({ message, mentionRegex, ownerName, }) {
    const initial = (message.author_name || "?").slice(0, 1).toUpperCase();
    return (_jsxs("div", { style: { display: "flex", gap: 8, marginBottom: 10 }, children: [_jsx("div", { "aria-hidden": "true", style: {
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    background: "var(--bg-tertiary)",
                    border: "1px solid var(--border)",
                    color: "var(--text-secondary)",
                    marginTop: 2,
                }, children: initial }), _jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 6 }, children: [_jsx("span", { style: { fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }, children: message.author_name }), _jsx("span", { style: { fontSize: 10.5, color: "var(--text-muted)" }, children: new Date(message.created_at).toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit",
                                }) })] }), _jsx("div", { style: { fontSize: 13, color: "var(--text-primary)", lineHeight: 1.45, overflowWrap: "break-word" }, children: _jsx(MarkdownContent, { content: message.content, mentionRegex: mentionRegex, ownerName: ownerName }) })] })] }));
}
//# sourceMappingURL=ThreadPanel.js.map